/**
 * La lecture de l'éditeur vidéo : la tête de lecture et ses trois modes — à l'arrêt
 * (édition), lecture de l'utilisateur, lecture d'une plage (l'aperçu d'une animation qu'on
 * vient de poser). Sans DOM d'éditeur : l'hôte lui donne le lecteur (l'iframe de preview) et
 * des rappels ; c'est ce qui le rend testable.
 *
 * Le relais canvas ↔ lecteur est écrit ici, une fois. Les blocs existent deux fois : sur le
 * canvas d'édition (fabric) et dans le lecteur. Pour ne jamais montrer une image sans eux :
 * - au départ, le lecteur reçoit le document en mode lecture (il dessine les blocs) et ce
 *   n'est qu'après son rendu que le canvas s'efface ;
 * - au retour, le canvas réapparaît d'abord, puis — l'image suivante, une fois peint — le
 *   lecteur se replace et cesse de dessiner les blocs.
 *
 * Une plage n'est pas une lecture de l'utilisateur : le bouton ne bouge pas, la sélection et
 * la page de la toolbox restent ; le lecteur l'arrête lui-même sur sa dernière image (à
 * l'image près), la tête revient au début ; la moindre action (clic, touche) l'interrompt.
 */

export interface PlaybackPlayer {
  play(opts?: { muted?: boolean }): void
  pause(): void
  seekTo(frame: number): void
  playRange(from: number, to: number, opts?: { muted?: boolean }): void
}

export interface PlaybackHost {
  player: PlaybackPlayer
  fps(): number
  /** Envoie le document au lecteur (en mode lecture tant que `rendersBlocks`). */
  sync(): void
  /** Idem, résolu quand le lecteur l'a rendu. */
  syncAndWait(): Promise<void>
  /** Le canvas d'édition s'efface (false) ou revient (true). */
  showCanvas(visible: boolean, opts: { keepSelection: boolean }): void
  /** La tête de lecture a bougé (timeline, page sous la tête…). */
  onTime(t: number): void
  /** La lecture de l'utilisateur démarre ou s'arrête (le bouton). */
  onPlayingChange(playing: boolean): void
  /** Garder la sélection au départ d'une lecture de l'utilisateur, la rendre à la pause. */
  saveSelection(): void
  restoreSelection(): void
  /** L'image suivante (requestAnimationFrame) — injectable pour les tests. */
  nextFrame?: (fn: () => void) => void
  /** Où écouter les actions qui interrompent une plage — injectable pour les tests. */
  doc?: Pick<Document, "addEventListener" | "removeEventListener">
}

type Mode = "paused" | "playing" | "range"

interface Range { from: number, fromFrame: number, cleanup: () => void }

export class EditorPlayback {
  private mode: Mode = "paused"
  private range: Range | null = null
  private t = 0

  constructor(private host: PlaybackHost) {}

  /** La tête de lecture, en secondes. */
  get time(): number { return this.t }

  /** La lecture de l'utilisateur est en cours. */
  get playing(): boolean { return this.mode === "playing" }

  /** Le lecteur dessine les blocs (lecture ou plage) — le mode du document qu'on lui envoie. */
  get rendersBlocks(): boolean { return this.mode !== "paused" }

  /** La tête bouge sans que le lecteur suive (un glissé sur la règle, déjà en train de chercher). */
  setTime(t: number): void {
    this.t = t
  }

  /** La tête et le lecteur vont à `t`. */
  goTo(t: number): void {
    this.t = t
    this.host.onTime(t)
    this.host.player.seekTo(this.frame(t))
  }

  /** Le lecteur annonce l'image qu'il affiche. */
  frameUpdate(frame: number): void {
    this.t = frame / this.host.fps()
    this.host.onTime(this.t)
  }

  /** Lecture / pause de l'utilisateur. Une plage en cours s'interrompt d'abord. */
  toggle(): void {
    this.stopRange()
    if (this.mode === "playing") this.pause()
    else this.play()
  }

  /**
   * Joue [from, to] (secondes) et s'arrête seul à la fin, de retour au début. Rien pendant
   * une lecture de l'utilisateur ; une nouvelle plage remplace la précédente.
   */
  playRange(from: number, to: number, { muted = false } = {}): void {
    if (this.mode === "playing" || !(to > from)) return
    this.stopRange()

    const doc = this.host.doc ?? document
    const interrupt = () => this.stopRange()
    doc.addEventListener("pointerdown", interrupt, { capture: true })
    doc.addEventListener("keydown", interrupt, { capture: true })
    const range: Range = {
      from,
      fromFrame: this.frame(from),
      cleanup: () => {
        doc.removeEventListener("pointerdown", interrupt, { capture: true })
        doc.removeEventListener("keydown", interrupt, { capture: true })
      },
    }
    this.range = range
    this.mode = "range"

    this.host.player.playRange(range.fromFrame, this.frame(to), { muted })
    this.host.syncAndWait().then(() => {
      if (this.range === range) this.host.showCanvas(false, { keepSelection: true })
    })
  }

  /** Interrompt la plage en cours : le lecteur s'arrête, l'éditeur reprend la main. */
  stopRange(): void {
    if (!this.range) return
    this.host.player.pause()
    this.rangeEnded()
  }

  /** La plage est finie (le lecteur l'annonce) ou interrompue : retour au début, canvas rendu. */
  rangeEnded(): void {
    const range = this.range
    if (!range) return
    this.range = null
    this.mode = "paused"
    range.cleanup()

    this.t = range.from
    this.host.onTime(range.from)
    this.handBack({ keepSelection: true }, () => this.host.player.seekTo(range.fromFrame))
  }

  destroy(): void {
    this.range?.cleanup()
    this.range = null
  }

  private play(): void {
    this.mode = "playing"
    this.host.onPlayingChange(true)
    this.host.saveSelection()
    this.host.player.play()
    this.host.syncAndWait().then(() => {
      if (this.mode === "playing") this.host.showCanvas(false, { keepSelection: false })
    })
  }

  private pause(): void {
    this.mode = "paused"
    this.host.onPlayingChange(false)
    this.host.player.pause()
    this.handBack({ keepSelection: false })
    this.host.restoreSelection()
  }

  /** Le canvas revient d'abord ; l'image suivante, une fois peint, le lecteur cesse de
   *  dessiner les blocs (après s'être replacé, s'il le faut). */
  private handBack(opts: { keepSelection: boolean }, beforeSync?: () => void): void {
    this.host.showCanvas(true, opts)
    const nextFrame = this.host.nextFrame ?? ((fn) => requestAnimationFrame(fn))
    nextFrame(() => {
      beforeSync?.()
      this.host.sync()
    })
  }

  private frame(t: number): number {
    return Math.round(t * this.host.fps())
  }
}
